import { useState, useRef } from 'react'
import { HexColorPicker } from 'react-colorful'
import { Input, PriceInput } from '../../../shared/components'
import Button from '../../../shared/components/Button'
import type { PropertyData, SizeData, MaterialData, ColorData } from '../../../pages/AddProductPage/types'
import { useCreateSize, useCreateMaterial, useCreateColor } from '../hooks/useMetaData'

interface Material {
  id: string
  name: string
}

interface Size {
  id: string
  name: string
}

interface PropertiesModalProps {
  isOpen: boolean
  onClose: () => void
  onSave?: () => void
  isEditMode?: boolean
  currentStep: number
  setCurrentStep: (step: number) => void
  propertyData: PropertyData
  setPropertyData: (data: PropertyData | ((prev: PropertyData) => PropertyData)) => void
  selectedSize: string
  setSelectedSize: (size: string) => void
  selectedMaterial: string
  setSelectedMaterial: (material: string) => void
  selectedColor: string
  setSelectedColor: (color: string) => void
  getAvailableSizes: () => Size[]
  getAvailableMaterials: () => string[]
  getAvailableColors: () => string[]
  getSizeCostPrice: () => number
  getSizePrice: () => number
  getMaterialCostPrice: () => number
  getMaterialPrice: () => number
  getColorCostPrice: () => number
  getColorPrice: () => number
  getCurrentQuantity: () => number | undefined
  getCurrentImages: () => (File | string)[]
  getCurrentWeight: () => number
  handleSizeCostPriceChange: (value: string) => void
  handleSizePriceChange: (value: string) => void
  handleMaterialCostPriceChange: (value: string) => void
  handleMaterialPriceChange: (value: string) => void
  handleColorCostPriceChange: (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => void
  handleColorPriceChange: (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => void
  handleQuantityChange: (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => void
  handleWeightChange: (value: string, targetSize?: string, targetMaterial?: string, targetColor?: string) => void
  handleImageChange: (files: File[], targetSize?: string, targetMaterial?: string, targetColor?: string) => void
  handleImageDelete: (index: number, targetSize?: string, targetMaterial?: string, targetColor?: string) => void
  materials: Material[]
  colorOptions: { name: string; value: string }[]
}

export const PropertiesModal = ({
  isOpen,
  onClose,
  onSave,
  isEditMode = false,
  currentStep,
  propertyData,
  setPropertyData,
  selectedSize,
  setSelectedSize,
  selectedMaterial,
  setSelectedMaterial,
  selectedColor,
  setSelectedColor,
  getAvailableSizes,
  getAvailableMaterials,
  getAvailableColors,
  getSizeCostPrice,
  getSizePrice,
  getMaterialCostPrice,
  getMaterialPrice,
  getColorCostPrice,
  getColorPrice,
  getCurrentQuantity,
  getCurrentImages,
  getCurrentWeight,
  handleSizeCostPriceChange,
  handleSizePriceChange,
  handleMaterialCostPriceChange,
  handleMaterialPriceChange,
  handleColorCostPriceChange,
  handleColorPriceChange,
  handleQuantityChange,
  handleWeightChange,
  handleImageChange,
  handleImageDelete,
  materials,
  colorOptions
}: PropertiesModalProps) => {
  const createSizeMutation = useCreateSize()
  const createMaterialMutation = useCreateMaterial()
  const createColorMutation = useCreateColor()

  const [addingSize, setAddingSize] = useState(false)
  const [newSizeName, setNewSizeName] = useState('')
  const [addingMaterial, setAddingMaterial] = useState(false)
  const [newMaterialName, setNewMaterialName] = useState('')
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [newColorName, setNewColorName] = useState('')
  const [newColorHex, setNewColorHex] = useState('#000000')
  const sizeInputRef = useRef<HTMLInputElement>(null)
  const materialInputRef = useRef<HTMLInputElement>(null)
  const colorNameInputRef = useRef<HTMLInputElement>(null)

  const handleAddSize = async () => {
    if (!newSizeName.trim()) return
    const order = getAvailableSizes().length + 1
    await createSizeMutation.mutateAsync({ name: newSizeName.trim(), order })
    setNewSizeName('')
    setAddingSize(false)
  }

  const handleAddMaterial = async () => {
    if (!newMaterialName.trim()) return
    await createMaterialMutation.mutateAsync(newMaterialName.trim())
    setNewMaterialName('')
    setAddingMaterial(false)
  }

  const handleAddColor = async () => {
    if (!newColorName.trim()) return
    await createColorMutation.mutateAsync({ name: newColorName.trim(), hexValue: newColorHex })
    setNewColorName('')
    setNewColorHex('#000000')
    setShowColorPicker(false)
  }

  if (!isOpen) return null

  // Filter options based on edit mode
  const availableSizes = isEditMode 
    ? getAvailableSizes().filter(size => propertyData.sizes.some(s => s.sizeId === size.id))
    : getAvailableSizes()
    
  const availableMaterials = isEditMode
    ? materials.filter(material => getAvailableMaterials().includes(material.name))
    : materials
    
  const availableColors = isEditMode
    ? colorOptions.filter(color => getAvailableColors().includes(color.value))
    : colorOptions

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-in fade-in duration-300">
      <div className="bg-card rounded-[25px] p-7 max-w-6xl max-h-[90vh] overflow-y-auto w-full mx-4 animate-in slide-in-from-bottom-4 duration-300">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-[18px] font-[600]">Add Properties</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div className="space-y-6 space-x-6 flex justify-start items-start">
          <div className='flex flex-col justify-center items-center space-y-6 mt-7'>
            <div className={`w-[20px] h-[20px] flex justify-center items-center rounded-full text-[13px] text-[#FFFFFF] ${currentStep >= 0 ? 'bg-[#3F3E42]' : 'bg-[#B8B5B6]'}`}>1</div>
            <div className='bg-[#B8B5B6] w-[2px] h-[135px]'></div>
            <div className={`w-[20px] h-[20px] flex justify-center items-center rounded-full text-[13px] text-[#FFFFFF] ${currentStep >= 1 ? 'bg-[#3F3E42]' : 'bg-[#B8B5B6]'}`}>2</div>
            <div className='bg-[#B8B5B6] w-[2px] h-[135px]'></div>
            <div className={`w-[20px] h-[20px] flex justify-center items-center rounded-full text-[13px] text-[#FFFFFF] ${currentStep >= 2 ? 'bg-[#3F3E42]' : 'bg-[#B8B5B6]'}`}>3</div>
          </div>

          <div className='space-y-10 w-full'>
            {/* Size */}
            <div>
              <div className="flex items-center gap-6 mb-4">
                <h3 className="font-[600] text-[14px]">Size</h3>
              </div>
              <div className="flex gap-14 mb-6">
                {availableSizes.map((size: Size) => {
                  const isSelected = propertyData.sizes.some((s: SizeData) => s.sizeId === size.id)
                  const isActive = selectedSize === size.id
                  const canSelect = true
                  
                  return (
                    <button
                      key={size.id}
                      disabled={!canSelect}
                      onClick={() => {
                        if (isSelected) {
                          if (selectedSize === size.id) {
                            if (!isEditMode) {
                              setPropertyData((prev: PropertyData) => ({
                                ...prev,
                                sizes: prev.sizes.filter((s: SizeData) => s.sizeId !== size.id)
                              }))
                              const remainingSizes = propertyData.sizes.filter((s: SizeData) => s.sizeId !== size.id)
                              setSelectedSize(remainingSizes.length > 0 ? remainingSizes[0].sizeId : '')
                              setSelectedMaterial('')
                              setSelectedColor('')
                            }
                          } else {
                            setSelectedSize(size.id)
                            const sizeData = propertyData.sizes.find((s: SizeData) => s.sizeId === size.id)
                            if (sizeData && sizeData.materials.length > 0) {
                              setSelectedMaterial(sizeData.materials[0].name)
                              if (sizeData.materials[0].colors.length > 0) {
                                setSelectedColor(sizeData.materials[0].colors[0].hex)
                              } else {
                                setSelectedColor('')
                              }
                            } else {
                              setSelectedMaterial('')
                              setSelectedColor('')
                            }
                          }
                        } else if (!isEditMode) {
                          setPropertyData((prev: PropertyData) => ({
                            ...prev,
                            sizes: [...prev.sizes, { sizeId: size.id, additionalCostPrice: 0, price: 0, materials: [] }]
                          }))
                          setSelectedSize(size.id)
                          setSelectedMaterial('')
                          setSelectedColor('')
                        }
                      }}
                      className={`px-4 py-2 rounded border w-[60px] h-[40px] text-[14px] ${!canSelect
                        ? 'bg-muted text-muted-foreground border-border cursor-not-allowed'
                        : isActive
                        ? 'bg-[#3F3E42] text-[#FBFBFE] border-black'
                        : 'bg-card text-foreground border-[#D9D9D9]'
                      }`}
                    >
                      {size.name}
                    </button>
                  )
                })}

                {/* Add new size */}
                {!isEditMode && (
                  addingSize ? (
                    <div className="flex items-center gap-1">
                      <input
                        ref={sizeInputRef}
                        value={newSizeName}
                        onChange={e => setNewSizeName(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddSize()}
                        placeholder="e.g. 4XL"
                        className="w-[60px] h-[40px] px-2 text-[14px] border border-[#D9D9D9] rounded text-center outline-none"
                        autoFocus
                      />
                      <button
                        onClick={handleAddSize}
                        disabled={createSizeMutation.isPending}
                        className="w-[40px] h-[40px] rounded border bg-[#3F3E42] text-white flex items-center justify-center"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                          <path d="M13.3307 4L5.9974 11.3333L2.66406 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setAddingSize(true); setTimeout(() => sizeInputRef.current?.focus(), 0) }}
                      className="w-[60px] h-[40px] rounded border bg-[#3F3E42] text-white flex items-center justify-center text-[20px]"
                    >
                      +
                    </button>
                  )
                )}
              </div>
              {selectedSize && (
                <div className='flex gap-4'>
                  <div className='w-[175px]'>
                    <PriceInput
                      label="Additional cost price (₦)"
                      value={getSizeCostPrice()}
                      onChange={(kobo) => handleSizeCostPriceChange(kobo.toString())}
                      className=" h-[38px]"
                    />
                  </div>
                  <div className='w-[175px]'>
                    <PriceInput
                      label="Additional price (₦)"
                      value={getSizePrice()}
                      onChange={(kobo) => handleSizePriceChange(kobo.toString())}
                      className=" h-[38px]"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Material Type */}
            {selectedSize && getSizePrice() >= 0 && (
              <div className="animate-in slide-in-from-top-2 duration-300" data-section="material">
                <div className="flex items-center gap-6 mb-6">
                  <h3 className="font-[600] text-[14px]">Material type</h3>
                </div>
                <div className="flex flex-wrap gap-6 mb-4">
                  {availableMaterials.map((material) => {
                    const isExisting = getAvailableMaterials().includes(material.name)
                    const isActive = selectedMaterial === material.name
                    const canSelect = true
                    
                    return (
                      <button
                        key={material.id}
                        disabled={!canSelect}
                        onClick={() => {
                          if (isExisting) {
                            if (selectedMaterial === material.name) {
                              if (!isEditMode) {
                                // Remove material if clicking on currently selected material
                                setPropertyData((prev: PropertyData) => ({
                                  ...prev,
                                  sizes: prev.sizes.map((s: SizeData) => 
                                    s.sizeId === selectedSize
                                      ? { ...s, materials: s.materials.filter((m: MaterialData) => m.name !== material.name) }
                                      : s
                                  )
                                }))
                                const sizeData = propertyData.sizes.find((s: SizeData) => s.sizeId === selectedSize)
                                const remainingMaterials = sizeData?.materials.filter((m: MaterialData) => m.name !== material.name) || []
                                if (remainingMaterials.length > 0) {
                                  setSelectedMaterial(remainingMaterials[0].name)
                                  if (remainingMaterials[0].colors.length > 0) {
                                    setSelectedColor(remainingMaterials[0].colors[0].hex)
                                  } else {
                                    setSelectedColor('')
                                  }
                                } else {
                                  setSelectedMaterial('')
                                  setSelectedColor('')
                                }
                              }
                            } else {
                              // Switch to existing material
                              setSelectedMaterial(material.name)
                              const sizeData = propertyData.sizes.find((s: SizeData) => s.sizeId === selectedSize)
                              const materialData = sizeData?.materials.find((m: MaterialData) => m.name === material.name)
                              if (materialData && materialData.colors.length > 0) {
                                setSelectedColor(materialData.colors[0].hex)
                              } else {
                                setSelectedColor('')
                              }
                            }
                          } else if (selectedSize && !isEditMode) {
                            // Add new material
                            setPropertyData((prev: PropertyData) => ({
                              ...prev,
                              sizes: prev.sizes.map((s: SizeData) => 
                                s.sizeId === selectedSize
                                  ? { ...s, materials: [...s.materials, { name: material.name, additionalCostPrice: 0, price: 0, colors: [] }] }
                                  : s
                              )
                            }))
                            setSelectedMaterial(material.name)
                            setSelectedColor('')
                          }
                        }}
                        className={`px-4 py-2 rounded-[10px] w-[175px] border ${!canSelect
                          ? 'bg-muted text-muted-foreground border-border cursor-not-allowed'
                          : isActive
                          ? 'bg-[#3F3E42] text-white border-black'
                          : 'bg-muted text-foreground border-[#D9D9D9]'
                        }`}
                      >
                        {material.name}
                      </button>
                    )
                  })}

                  {/* Add new material */}
                  {!isEditMode && (
                    addingMaterial ? (
                      <div className="flex items-center gap-1">
                        <input
                          ref={materialInputRef}
                          value={newMaterialName}
                          onChange={e => setNewMaterialName(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleAddMaterial()}
                          placeholder="Material name"
                          className="w-[140px] h-[38px] px-3 text-[14px] border border-[#D9D9D9] rounded-[10px] outline-none"
                          autoFocus
                        />
                        <button
                          onClick={handleAddMaterial}
                          disabled={createMaterialMutation.isPending}
                          className="w-[38px] h-[38px] rounded-[10px] bg-[#3F3E42] text-white flex items-center justify-center"
                        >
                          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                            <path d="M13.3307 4L5.9974 11.3333L2.66406 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setAddingMaterial(true); setTimeout(() => materialInputRef.current?.focus(), 0) }}
                        className="w-[50px] h-[38px] rounded-[10px] bg-[#3F3E42] text-white flex items-center justify-center text-[20px]"
                      >
                        +
                      </button>
                    )
                  )}
                </div>
                {selectedMaterial && (
                  <div className='flex gap-4'>
                    <div className='w-[175px]'>
                      <PriceInput
                        label="Additional cost price (₦)"
                        value={getMaterialCostPrice()}
                        onChange={(kobo) => handleMaterialCostPriceChange(kobo.toString())}
                        className="w-32 h-[38px]"
                      />
                    </div>
                    <div className='w-[175px]'>
                      <PriceInput
                        label="Additional price (₦)"
                        value={getMaterialPrice()}
                        onChange={(kobo) => handleMaterialPriceChange(kobo.toString())}
                        className="w-32 h-[38px]"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Color */}
            {selectedMaterial && getMaterialPrice() >= 0 && (
              <div className="animate-in slide-in-from-top-2 duration-300" data-section="color">
                <div className="flex items-center gap-6 mb-4">
                  <h3 className="text-muted-foreground font-[600] text-[14px]">Color</h3>
                </div>
                <div className="flex gap-4 mb-4">
                  {availableColors.map((color) => {
                    const isExisting = getAvailableColors().includes(color.value)
                    const isActive = selectedColor === color.value
                    const canSelect = true
                    
                    return (
                      <div key={color.value} className="relative text-center p-3">
                        <p className={`text-xs mb-1 ${isActive
                          ? 'font-[700] text-[13px]'
                          : canSelect ? 'font-[400] text-[13px]' : 'font-[400] text-[13px] text-muted-foreground'
                          }`}>{color.name}</p>
                        <div className={`flex justify-center w-[44px] h-[44px] rounded-full items-center ${isActive
                          ? 'border-[#7C797C] border-[2.5px]'
                          : ''
                          }`}>
                          <button
                            disabled={!canSelect}
                            onClick={() => {
                              if (isExisting) {
                                if (selectedColor === color.value) {
                                  if (!isEditMode) {
                                    setPropertyData((prev: PropertyData) => ({
                                      ...prev,
                                      sizes: prev.sizes.map((s: SizeData) => 
                                        s.sizeId === selectedSize
                                          ? {
                                              ...s,
                                              materials: s.materials.map((m: MaterialData) => 
                                                m.name === selectedMaterial
                                                  ? { ...m, colors: m.colors.filter((c: ColorData) => c.hex !== color.value) }
                                                  : m
                                              )
                                            }
                                          : s
                                      )
                                    }))
                                    const sizeData = propertyData.sizes.find((s: SizeData) => s.sizeId === selectedSize)
                                    const materialData = sizeData?.materials.find((m: MaterialData) => m.name === selectedMaterial)
                                    const remainingColors = materialData?.colors.filter((c: ColorData) => c.hex !== color.value) || []
                                    setSelectedColor(remainingColors.length > 0 ? remainingColors[0].hex : '')
                                  }
                                } else {
                                  setSelectedColor(color.value)
                                }
                              } else if (selectedSize && selectedMaterial && !isEditMode) {
                                const colorFromOptions = colorOptions.find(c => c.value === color.value)
                                setPropertyData((prev: PropertyData) => ({
                                  ...prev,
                                  sizes: prev.sizes.map((s: SizeData) => 
                                    s.sizeId === selectedSize
                                      ? {
                                          ...s,
                                          materials: s.materials.map((m: MaterialData) => 
                                            m.name === selectedMaterial
                                              ? {
                                                  ...m,
                                                  colors: [...m.colors, {
                                                    id: colorFromOptions?.value || color.value,
                                                    name: color.name,
                                                    hex: color.value,
                                                    additionalCostPrice: 0,
                                                    price: 0,
                                                    quantity: 0,
                                                    weight: 0,
                                                    images: [],
                                                    imageUrls: []
                                                  }]
                                                }
                                              : m
                                          )
                                        }
                                      : s
                                  )
                                }))
                                setSelectedColor(color.value)
                              }
                            }}
                            className={`w-[30px] h-[30px] rounded-full ${canSelect ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                            style={{ backgroundColor: color.value }}
                          />
                        </div>
                      </div>
                    )
                  })}

                  {/* Add new color */}
                  {!isEditMode && (
                    <div className="relative text-center p-3">
                      <p className="text-xs mb-1 font-[400] text-[13px] opacity-0">+</p>
                      <div className="flex justify-center w-[44px] h-[44px] rounded-full items-center">
                        <button
                          onClick={() => setShowColorPicker(true)}
                          className="w-[30px] h-[30px] rounded-full bg-[#3F3E42] text-white flex items-center justify-center text-[18px]"
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {selectedColor && (
                  <div className="flex gap-x-6 items-center">
                    <PriceInput
                      label="Additional cost price (₦)"
                      value={getColorCostPrice()}
                      onChange={(kobo) => {
                        const materialToUse = selectedMaterial || getAvailableMaterials()[0]
                        const colorToUse = selectedColor || getAvailableColors()[0]
                        handleColorCostPriceChange(kobo.toString(), selectedSize, materialToUse, colorToUse)
                      }}
                      className="w-32 h-[38px]"
                    />
                    <PriceInput
                      label="Additional price (₦)"
                      value={getColorPrice()}
                      onChange={(kobo) => {
                        const materialToUse = selectedMaterial || getAvailableMaterials()[0]
                        const colorToUse = selectedColor || getAvailableColors()[0]
                        handleColorPriceChange(kobo.toString(), selectedSize, materialToUse, colorToUse)
                      }}
                      className="w-32 h-[38px]"
                    />
                    <Input
                      label="Quantity"
                      type="number"
                      value={getCurrentQuantity()?.toString()}
                      onChange={(e) => {
                        const materialToUse = selectedMaterial || getAvailableMaterials()[0]
                        const colorToUse = selectedColor || getAvailableColors()[0]
                        handleQuantityChange(e.target.value, selectedSize, materialToUse, colorToUse)
                      }}
                      className="w-32 h-[38px]"
                    />
                    <Input
                      label="Weight (kg)"
                      type="number"
                      placeholder="weight in kg"
                      value={getCurrentWeight()?.toString()}
                      onChange={(e) => {
                        const materialToUse = selectedMaterial || getAvailableMaterials()[0]
                        const colorToUse = selectedColor || getAvailableColors()[0]
                        handleWeightChange(e.target.value, selectedSize, materialToUse, colorToUse)
                      }}
                      className="w-32 h-[38px]"
                    />

                    {/* Image Upload */}
                    <div className="flex gap-6 mt-4">
                      {getCurrentImages().map((image, index) => (
                        <div key={index} className="relative w-[84px] h-[87px] rounded-[10px] border">
                          <img
                            src={typeof image === 'string' ? image : URL.createObjectURL(image)}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover rounded"
                          />
                          <button
                            onClick={() => {
                              const materialToUse = selectedMaterial || getAvailableMaterials()[0]
                              const colorToUse = selectedColor || getAvailableColors()[0]
                              handleImageDelete(index, selectedSize, materialToUse, colorToUse)
                            }}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-muted rounded-full shadow-lg shadow-[#03020833] flex items-center justify-center text-xs hover:bg-muted"
                          >
                            <svg width="11" height="12" viewBox="0 0 11 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <g clipPath="url(#clip0_3134_27619)">
                                <path d="M1.45312 3H2.28646H8.95312" stroke="#7C797C" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M8.11458 2.99976V9.99976C8.11458 10.265 8.02679 10.5193 7.87051 10.7069C7.71423 10.8944 7.50226 10.9998 7.28125 10.9998H3.11458C2.89357 10.9998 2.68161 10.8944 2.52533 10.7069C2.36905 10.5193 2.28125 10.265 2.28125 9.99976V2.99976M3.53125 2.99976V1.99976C3.53125 1.73454 3.61905 1.48019 3.77533 1.29265C3.93161 1.10511 4.14357 0.999756 4.36458 0.999756H6.03125C6.25226 0.999756 6.46423 1.10511 6.62051 1.29265C6.77679 1.48019 6.86458 1.73454 6.86458 1.99976V2.99976" stroke="#7C797C" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M4.375 5.49976V8.49976" stroke="#7C797C" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M6.03125 5.49976V8.49976" stroke="#7C797C" strokeLinecap="round" strokeLinejoin="round" />
                              </g>
                              <defs>
                                <clipPath id="clip0_3134_27619">
                                  <rect width="10" height="12" fill="white" transform="translate(0.203125)" />
                                </clipPath>
                              </defs>
                            </svg>
                          </button>
                        </div>
                      ))}

                      {getCurrentImages().length < 3 && (
                        <label className="w-[102px] h-[90px] rounded-[10px] bg-muted border-[0.89px] border-dashed border-[#B8B5B6] flex flex-col items-center justify-center cursor-pointer hover:bg-muted">
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || [])
                              const materialToUse = selectedMaterial || getAvailableMaterials()[0]
                              const colorToUse = selectedColor || getAvailableColors()[0]
                              handleImageChange(files, selectedSize, materialToUse, colorToUse)
                            }}
                          />
                          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clipPath="url(#clip0_3134_27635)">
                              <path d="M11.9572 11.852L8.99421 8.88904L6.03125 11.852" stroke="#7C797C" strokeWidth="1.48148" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M9 8.88904V15.5557" stroke="#7C797C" strokeWidth="1.48148" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M15.2107 13.6223C15.9331 13.2284 16.5039 12.6052 16.8328 11.8509C17.1617 11.0966 17.2301 10.2543 17.0271 9.45689C16.8241 8.65945 16.3614 7.95231 15.7119 7.44708C15.0624 6.94184 14.2632 6.66729 13.4403 6.66674H12.507C12.2827 5.79952 11.8649 4.9944 11.2847 4.31193C10.7045 3.62945 9.97721 3.08737 9.1574 2.72645C8.33759 2.36553 7.44662 2.19516 6.55149 2.22814C5.65635 2.26112 4.78034 2.4966 3.98931 2.91687C3.19828 3.33714 2.51282 3.93127 1.98446 4.65458C1.45609 5.3779 1.09858 6.21159 0.938795 7.09296C0.77901 7.97434 0.82111 8.88047 1.06193 9.74323C1.30275 10.606 1.73603 11.4029 2.32918 12.0742" stroke="#7C797C" strokeWidth="1.48148" strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M11.9572 11.852L8.99421 8.88904L6.03125 11.852" stroke="#7C797C" strokeWidth="1.48148" strokeLinecap="round" strokeLinejoin="round" />
                            </g>
                            <defs>
                              <clipPath id="clip0_3134_27635">
                                <rect width="17.7778" height="17.7778" fill="white" transform="translate(0.109375)" />
                              </clipPath>
                            </defs>
                          </svg>
                          <div className="text-center mt-1 text-[11px] text-muted-foreground font-[400]">
                            <p>Drag image or</p>
                            <p>click to upload</p>
                          </div>
                        </label>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex justify-end items-center pt-6 border-t">
              <Button variant="outline" onClick={onClose} size="sm" className='h-[32px] w-[150px]'>
                Back
              </Button>
              <Button onClick={onSave || onClose} size='sm' className='h-[32px] w-[150px] ml-4 px-3 text-[#FBFBFE] font-[700] text-[14px]'>
                Save properties
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[60]">
          <div className="bg-card rounded-[16px] p-6 w-[320px] shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-[600]">Color Picker</h3>
              <button onClick={() => setShowColorPicker(false)} className="text-muted-foreground hover:text-muted-foreground">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            <HexColorPicker color={newColorHex} onChange={setNewColorHex} style={{ width: '100%', height: '200px' }} />

            <div className="flex items-center gap-2 mt-4">
              <div className="w-8 h-8 rounded-full border border-[#D9D9D9] flex-shrink-0" style={{ backgroundColor: newColorHex }} />
              <span className="text-[13px] text-muted-foreground font-mono">{newColorHex}</span>
            </div>

            <div className="mt-4">
              <label className="block text-[13px] font-[600] text-foreground mb-1">Color Name</label>
              <input
                ref={colorNameInputRef}
                value={newColorName}
                onChange={e => setNewColorName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAddColor()}
                placeholder="e.g. Olive, Burgundy"
                className="w-full h-[38px] px-3 text-[14px] border border-[#D9D9D9] rounded-[8px] outline-none focus:border-[#3F3E42]"
                autoFocus
              />
            </div>

            <div className="flex gap-3 mt-4">
              <Button variant="outline" size="sm" onClick={() => setShowColorPicker(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleAddColor}
                disabled={!newColorName.trim() || createColorMutation.isPending}
                loading={createColorMutation.isPending}
                className="flex-1"
              >
                Save Color
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}